export type MediaDurationStatus = "idle" | "checking" | "ready" | "failed";

interface MediaDurationOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

export const getMediaDuration = (
  file: File,
  options?: MediaDurationOptions
): Promise<number | null> => {
  const { timeoutMs = 5000, signal } = options || {};

  return new Promise((resolve, reject) => {
    const elementTag = file.type.startsWith("video/") ? "video" : "audio";
    const element = document.createElement(elementTag);
    element.preload = "metadata";
    element.muted = true;

    if (elementTag === "video") {
      (element as HTMLVideoElement).playsInline = true;
    }

    const objectUrl = URL.createObjectURL(file);
    let timeoutId: number | null = null;

    const cleanup = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
      element.removeAttribute("src");
      URL.revokeObjectURL(objectUrl);
      element.onloadedmetadata = null;
      element.onerror = null;
      if (signal) {
        signal.removeEventListener("abort", handleAbort);
      }
    };

    const handleAbort = () => {
      cleanup();
      reject(new DOMException("Request aborted", "AbortError"));
    };

    if (signal) {
      if (signal.aborted) {
        handleAbort();
        return;
      }
      signal.addEventListener("abort", handleAbort);
    }

    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    element.onloadedmetadata = () => {
      cleanup();
      const duration = element.duration;
      if (Number.isFinite(duration) && duration > 0) {
        resolve(duration);
      } else {
        resolve(null);
      }
    };

    element.onerror = () => {
      cleanup();
      resolve(null);
    };

    element.src = objectUrl;
  });
};
